import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  ActiveFilterBar,
  activeFilterChip,
  type ActiveFilterChip,
} from "../../components/common/ActiveFilterBar/ActiveFilterBar.js";
import { Button } from "../../components/ui/button/index.js";
import { cn } from "../../lib/utils.js";
import { ReportDateRangeControl } from "./ReportDateRangeControl.js";
import {
  ReportOpenFiltersButton,
  ReportFilterSheet,
} from "./ReportFilterSheet.js";
import { ReportMoreFilters } from "./ReportMoreFilters.js";

/**
 * Compact shared report toolbar row:
 * search (narrow) · columns · dates · more filters · mobile filters · reset
 */
export function ReportToolbarRow({
  search,
  onSearchChange,
  searchPlaceholder,
  searchTestId,
  dateFrom,
  dateTo,
  onDateRangeChange,
  fromTestId,
  toTestId,
  columnsSlot,
  moreFilters,
  moreFiltersCount = 0,
  moreFiltersTestId,
  mobileFiltersOpen,
  onMobileFiltersOpenChange,
  mobileFiltersTitle,
  mobileFiltersDescription,
  mobileFiltersTestId,
  mobileFiltersOpenTestId,
  mobileFiltersBody,
  filtersActive,
  onResetFilters,
  activeFilters = [],
  explicitDateFrom,
  explicitDateTo,
  resetTestId,
  filteredCountSlot,
  className,
  toolbarTestId,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  searchTestId: string;
  dateFrom: string;
  dateTo: string;
  onDateRangeChange: (from: string, to: string) => void;
  fromTestId?: string;
  toTestId?: string;
  columnsSlot?: React.ReactNode;
  moreFilters?: React.ReactNode;
  moreFiltersCount?: number;
  moreFiltersTestId?: string;
  mobileFiltersOpen: boolean;
  onMobileFiltersOpenChange: (open: boolean) => void;
  mobileFiltersTitle: string;
  mobileFiltersDescription: string;
  mobileFiltersTestId: string;
  mobileFiltersOpenTestId: string;
  mobileFiltersBody: React.ReactNode;
  filtersActive: boolean;
  onResetFilters: () => void;
  activeFilters?: readonly ActiveFilterChip[];
  /** Set only when the user chose a date, not the default report range. */
  explicitDateFrom?: string | null;
  explicitDateTo?: string | null;
  resetTestId: string;
  filteredCountSlot?: React.ReactNode;
  className?: string;
  toolbarTestId: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const chips = [
    activeFilterChip("search", t("reports.chrome.search"), search, () => onSearchChange("")),
    activeFilterChip(
      "dates",
      t("reports.chrome.dateRange"),
      explicitDateFrom || explicitDateTo
        ? [explicitDateFrom, explicitDateTo].filter(Boolean).join(" – ")
        : null,
      () => onDateRangeChange("", ""),
    ),
    ...activeFilters,
  ].flatMap((chip) => (chip ? [chip] : []));

  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center gap-2 border-b border-line/50 bg-surface/80 px-4 py-2.5",
        className,
      )}
      data-testid={toolbarTestId}
    >
      <label className="relative block w-full max-w-[11rem] shrink-0 sm:max-w-[13rem]">
        <span className="sr-only">{t("reports.chrome.search")}</span>
        <Search
          size={13}
          className="pointer-events-none absolute start-2 top-1/2 -translate-y-1/2 text-muted"
          aria-hidden="true"
        />
        <input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          className="h-8 w-full rounded-md border border-line/70 bg-surface ps-7 pe-2 text-[0.75rem] text-foreground placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/50"
          data-testid={searchTestId}
        />
      </label>

      {columnsSlot}

      <ReportDateRangeControl
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateRangeChange={onDateRangeChange}
        fromTestId={fromTestId}
        toTestId={toTestId}
      />

      {moreFilters ? (
        <ReportMoreFilters
          activeCount={moreFiltersCount}
          testId={moreFiltersTestId}
        >
          {moreFilters}
        </ReportMoreFilters>
      ) : null}

      <ReportOpenFiltersButton
        onClick={() => onMobileFiltersOpenChange(true)}
        activeCount={moreFiltersCount}
        testId={mobileFiltersOpenTestId}
        label={t("reports.chrome.filters")}
      />

      {filteredCountSlot}

      {filtersActive && chips.length === 0 ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 shrink-0 gap-1 text-[0.75rem]"
          onClick={onResetFilters}
          data-testid={resetTestId}
        >
          <X size={14} aria-hidden="true" />
          {t("reports.chrome.resetFilters")}
        </Button>
      ) : null}

      <ReportFilterSheet
        open={mobileFiltersOpen}
        onOpenChange={onMobileFiltersOpenChange}
        title={mobileFiltersTitle}
        description={mobileFiltersDescription}
        testId={mobileFiltersTestId}
        activeCount={moreFiltersCount}
      >
        {mobileFiltersBody}
      </ReportFilterSheet>
      <ActiveFilterBar chips={chips} onClearAll={onResetFilters} clearTestId={resetTestId} className="basis-full border-0 bg-transparent px-0 py-0" />
    </div>
  );
}
